import type { SpaceMember } from "@ably/spaces";
import { getDeviceId } from "./utils";

export interface TabPresenceData {
  tabId: string;
  startedAt: number;
}

export class SingleTabEnforcer {
  private hasBeenKickedOut = false;
  private readonly currentTabId: string;
  private readonly currentStartedAt: number;
  private readonly onKickOut: () => void;

  constructor(onKickOut: () => void) {
    this.currentTabId = getDeviceId();
    this.currentStartedAt = Date.now();
    this.onKickOut = onKickOut;
  }

  getTabPresenceData(): TabPresenceData {
    return {
      tabId: this.currentTabId,
      startedAt: this.currentStartedAt,
    };
  }

  /**
   * Check if current tab should remain active based on presence members
   * Winner is determined by: highest startedAt, tie-break by tabId (string compare)
   */
  checkAndEnforce(members: SpaceMember[], currentUserId: string): void {
    if (this.hasBeenKickedOut) return;

    const myTabs = members.filter((member) => {
      const profileData = member.profileData as any;
      return profileData?.userId === currentUserId && member.isConnected;
    });

    if (myTabs.length <= 1) return;

    const winner = this.determineWinner(myTabs);
    const winnerData = winner.profileData as any;

    const isCurrentTabWinner = winnerData?.tabId === this.currentTabId && winnerData?.startedAt === this.currentStartedAt;

    if (!isCurrentTabWinner) {
      this.kickOut();
    }
  }

  private determineWinner(tabs: SpaceMember[]): SpaceMember {
    return tabs.reduce((winner, current) => {
      const winnerData = winner.profileData as any;
      const currentData = current.profileData as any;

      const winnerStartedAt = winnerData?.startedAt ?? 0;
      const currentStartedAt = currentData?.startedAt ?? 0;

      // Higher startedAt wins
      if (currentStartedAt > winnerStartedAt) return current;
      if (currentStartedAt < winnerStartedAt) return winner;

      // Tie-break by tabId (string compare)
      const winnerTabId = winnerData?.tabId ?? "";
      const currentTabId = currentData?.tabId ?? "";
      return currentTabId > winnerTabId ? current : winner;
    });
  }

  private kickOut(): void {
    if (this.hasBeenKickedOut) return;

    this.hasBeenKickedOut = true;
    this.onKickOut();
  }

  reset(): void {
    this.hasBeenKickedOut = false;
  }
}
